import { useEffect, useState } from 'react'
import type { Policy } from '@sh/shared'
import { PolicyTile } from './Board.js'
import { HoldToConfirm } from './Confirm.js'

/**
 * A hand of policies to look at and nothing more: the three a Policy Peek
 * shows, and the same hand in Storybook. The hand you have to *choose* from is
 * `PolicyChoice` below, because there the cards are the buttons.
 *
 * The layout is three columns wide whatever it is given, so two cards occupy
 * two thirds and sit centred rather than stretching to fill. A card that grew
 * when the Chancellor held two instead of three read as a different card.
 *
 * The position number sits in the card's bottom corner rather than on a line of
 * its own above it. It was costing a whole row of a screen that is held up with
 * one thumb, and the room it was taking is room the cards now have.
 */
export function PolicyHand({ policies }: { policies: Policy[] }) {
  return (
    <span className="hand">
      {policies.map((policy, i) => (
        <span key={i} className="hand__slot">
          {/*
            Before the card in the DOM so a screen reader still reads "1,
            fascist policy" in the order the cards are numbered, and painted
            over it by z-index rather than by source order.
          */}
          <span className="hand__index">{i + 1}</span>
          <PolicyTile policy={policy} size="lg" />
        </span>
      ))}
    </span>
  )
}

/** The face of a card that is not being shown. Pure decoration - it is `aria-hidden`. */
function FaceDown() {
  return (
    <span className="hand__slot">
      <span className="tile tile--back">
        <span className="tile__back-mark">&#9679;&#9679;&#9679;</span>
      </span>
    </span>
  )
}

const VERB = { discard: 'Discard', enact: 'Enact' } as const

/**
 * The legislative hand: the cards, and the choice about them, as one control.
 *
 * Two properties are the whole design, and they are the same property twice:
 *
 *  - **You cannot act on a card you have not looked at.** There is no separate
 *    row of Discard buttons any more. The card *is* the button, so until the
 *    hand is shown there is nothing on the screen to press - a President who
 *    has not looked cannot discard by position, by habit, or by fumble. The
 *    old shape made that possible: the hand hid itself after a grace window
 *    and left three live buttons behind it.
 *  - **Showing is a press, not a hold.** A press and hold was right for a
 *    secret you only read - a role, a peek - because letting go is what puts
 *    it away. It is wrong for a secret you have to *act on*: it cost a hand,
 *    ran out from under you mid-thought, and meant aiming at a button for a
 *    card that was no longer on the screen. Here you show the cards, think,
 *    and press one.
 *  - **Once shown, the hand stays shown.** There is no way to put it back by
 *    hand: the show button goes inert and says what to do next. A Hide button
 *    sat right above the cards, so a thumb reaching for one could put the
 *    whole hand away mid-decision, and nothing about the choice is helped by
 *    being able to look away from it.
 *
 * What the hold bought is not given away. The cards are conditionally
 * rendered, so when hidden they are not in the DOM to be read at all, and they
 * still put themselves away the moment the phone does - tab switch, window
 * blur, page hide, screen lock - to be shown again when it comes back. Committing one is still the app's one hold-to-confirm
 * press, so a knuckle on a card enacts nothing.
 *
 * Face-down cards hold the hand's place while it is hidden. The row is the
 * tallest thing on the screen; collapsing it would move everything under it
 * twice per decision.
 */
export function PolicyChoice({
  policies,
  action,
  onChoose,
  testId,
}: {
  policies: Policy[]
  /** Which verb the cards carry, and what the engine is told when one is pressed. */
  action: 'discard' | 'enact'
  onChoose: (index: number) => void
  testId: string
}) {
  const [shown, setShown] = useState(false)
  const cardsId = `${testId}-cards`

  // Put the hand away whenever the screen stops being yours. Nothing else
  // hides it - not a release, not a button - so this is the only thing
  // standing between a shown hand and the person leaning over your shoulder.
  useEffect(() => {
    if (!shown) return
    const hide = () => setShown(false)
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hide()
    }
    window.addEventListener('blur', hide)
    window.addEventListener('pagehide', hide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', hide)
      window.removeEventListener('pagehide', hide)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [shown])

  return (
    <div className="pick" data-testid={testId} data-revealed={shown ? 'true' : 'false'}>
      <button
        type="button"
        className={`btn btn--block pick__toggle ${shown ? 'is-shown' : ''}`}
        aria-expanded={shown}
        aria-controls={cardsId}
        disabled={shown}
        data-testid={`${testId}-toggle`}
        onClick={() => setShown(true)}
      >
        {shown ? `Choose one to ${action}` : 'Show the policies'}
      </button>
      <div
        id={cardsId}
        className="hand pick__hand"
        data-testid={shown ? `${testId}-content` : undefined}
      >
        {policies.map((policy, i) =>
          shown ? (
            <HoldToConfirm
              key={i}
              className="pick__slot"
              onConfirm={() => onChoose(i)}
              testId={`${action}-${i}`}
            >
              <span className="hand__slot">
                <span className="hand__index">{i + 1}</span>
                <PolicyTile policy={policy} size="lg" />
              </span>
              <span className="pick__verb">{VERB[action]}</span>
            </HoldToConfirm>
          ) : (
            <span key={i} className="pick__slot pick__slot--back" aria-hidden="true">
              {/*
                The same inner wrapper the button gives its children, so the
                two states are the same box to the pixel and nothing moves
                when the hand is shown.
              */}
              <span className="btn__text">
                <FaceDown />
                <span className="pick__verb">Hidden</span>
              </span>
            </span>
          ),
        )}
      </div>
    </div>
  )
}
